import { supabase } from './supabase';
import type { BankTransaction, ApiResponse } from '../types/database';
import type { ExpectedAction } from './reconciliationService';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A stored auto-match rule.
 *
 * `target_recurring_line_id` points to ONE representative line inside
 * `recurring_actions_lines`.  The engine treats that line's
 * (description, amount) pair as a "group key" and will FIFO-match against
 * the oldest pending line in the same group — so the rule effectively targets
 * the whole recurring series, not just that single date.
 */
export interface BankMatchRule {
  id: string;
  company_id: string;
  /** Substring to search for inside bank-transaction descriptions (case-insensitive) */
  search_text: string;
  /** UUID of a representative `recurring_actions_lines` row (used for description/amount group key) */
  target_recurring_line_id: string;
  created_at: string;
  // Joined relation – populated by select queries
  recurring_actions_lines?: {
    description: string;
    amount: number;
  };
}

export interface CreateBankMatchRuleData {
  company_id: string;
  search_text: string;
  target_recurring_line_id: string;
}

/**
 * Confidence tier for an auto-suggested match.
 *
 *  1 – Custom Rule   : matched via a `bank_match_rules` entry
 *  2 – Smart Match   : same |amount| AND (description overlap OR same accounting category)
 *  3 – Amount Only   : same |amount|, no other signal
 */
export type MatchLevel = 1 | 2 | 3;

/** One auto-suggested match pair produced by generateMatchSuggestions */
export interface MatchSuggestion {
  /** null for Level-2/3 suggestions that were not triggered by a stored rule */
  ruleId: string | null;
  bankTx: BankTransaction;
  expectedAction: ExpectedAction;
  matchLevel: MatchLevel;
  /** Human-readable Hebrew explanation */
  matchReason: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Service  (CRUD for bank_match_rules table)
// ─────────────────────────────────────────────────────────────────────────────

class MatchRulesService {
  /** Fetch all rules for a company, joining the representative line for display. */
  async getMatchRules(companyId: string): Promise<ApiResponse<BankMatchRule[]>> {
    try {
      const { data, error } = await supabase
        .from('bank_match_rules')
        .select(`
          *,
          recurring_actions_lines (
            description,
            amount
          )
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data as BankMatchRule[], error: null, status: 200 };
    } catch (err) {
      console.error('getMatchRules error:', err);
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to fetch match rules' },
        status: 500,
      };
    }
  }

  /** Create a new rule. */
  async createMatchRule(rule: CreateBankMatchRuleData): Promise<ApiResponse<BankMatchRule>> {
    try {
      const { data, error } = await supabase
        .from('bank_match_rules')
        .insert(rule)
        .select(`
          *,
          recurring_actions_lines (
            description,
            amount
          )
        `)
        .single();

      if (error) throw error;
      return { data: data as BankMatchRule, error: null, status: 201 };
    } catch (err) {
      console.error('createMatchRule error:', err);
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to create match rule' },
        status: 500,
      };
    }
  }

  /** Delete a rule by id. */
  async deleteMatchRule(ruleId: string): Promise<ApiResponse<void>> {
    try {
      const { error } = await supabase
        .from('bank_match_rules')
        .delete()
        .eq('id', ruleId);

      if (error) throw error;
      return { data: null, error: null, status: 200 };
    } catch (err) {
      console.error('deleteMatchRule error:', err);
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to delete match rule' },
        status: 500,
      };
    }
  }
}

export const matchRulesService = new MatchRulesService();

// ─────────────────────────────────────────────────────────────────────────────
// Pure utility helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Normalise to 2 dp absolute value for amount comparisons. */
function roundAmount(n: number): number {
  return Math.round(Math.abs(n) * 100) / 100;
}

/**
 * Normalise a string for robust text matching:
 *  1. Trim leading/trailing whitespace.
 *  2. Collapse any run of internal whitespace into a single space.
 *  3. Lowercase.
 *
 * Example: "  סלקום   פרטי  " → "סלקום פרטי"
 */
function normaliseText(s: string): string {
  return (s ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * True when at least one non-trivial token (≥2 chars) from `b` appears in `a`.
 * Handles Hebrew and Latin text.
 */
function descriptionOverlap(a: string, b: string): boolean {
  const tokenise = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s]/gu, ' ')
      .split(/\s+/)
      .filter((t) => t.length >= 2);

  const setA = new Set(tokenise(a));
  return tokenise(b).some((t) => setA.has(t));
}

// ─────────────────────────────────────────────────────────────────────────────
// 3-Tier FIFO match engine
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates match suggestions using a 3-tier confidence system.
 *
 * **How Level-1 (Custom Rule) group-matching works:**
 *   - A rule stores `target_recurring_line_id` → a representative `recurring_actions_lines` row.
 *   - We look up that row's `(description, amount)` to form a *group key*.
 *   - The engine then finds the **oldest pending line** whose `(description, amount)` matches
 *     that group key — so one rule covers the whole repeating series, not just one date.
 *
 * **Tier 2 – Smart Match:** same |amount| AND (description word-overlap OR same category id).
 * **Tier 3 – Amount Only:** same |amount| only — unchecked by default.
 *
 * All three tiers share one action pool; FIFO is enforced globally.
 */
export function generateMatchSuggestions(
  unmatchedBankTx: BankTransaction[],
  unmatchedActions: ExpectedAction[],
  matchRules: BankMatchRule[]
): MatchSuggestion[] {
  if (!unmatchedBankTx.length || !unmatchedActions.length) return [];

  // ── Build shared action pool sorted FIFO (oldest action_date first) ──────
  const actionPool: ExpectedAction[] = [...unmatchedActions].sort((a, b) =>
    a.action_date.localeCompare(b.action_date)
  );
  const usedActionIds = new Set<string>();
  const usedBankTxIds = new Set<string>();

  // Sort bank txs oldest-first for deterministic pairing
  const sortedBankTx = [...unmatchedBankTx].sort((a, b) =>
    a.transaction_date.localeCompare(b.transaction_date)
  );

  const suggestions: MatchSuggestion[] = [];

  /**
   * Find and consume the oldest unconsumed action satisfying predicate.
   */
  function popOldest(predicate: (a: ExpectedAction) => boolean): ExpectedAction | undefined {
    for (const action of actionPool) {
      if (usedActionIds.has(action.id)) continue;
      if (predicate(action)) {
        usedActionIds.add(action.id);
        return action;
      }
    }
    return undefined;
  }

  // ── Build rule group-key index ────────────────────────────────────────────
  // For each rule, extract the group key (desc_lower + '||' + roundedAmt)
  // from the joined representative line.
  interface RuleWithKey {
    rule: BankMatchRule;
    descLower: string;
    amt: number;
  }
  const rulesWithKeys: RuleWithKey[] = matchRules
    .filter((r) => r.recurring_actions_lines)
    .map((r) => ({
      rule: r,
      descLower: normaliseText(r.recurring_actions_lines!.description ?? ''),
      amt: roundAmount(r.recurring_actions_lines!.amount),
    }));

  // ════════════════════════════════════════════════════════════════════════════
  // PASS 1 – Tier 1: Custom Rule
  //   Bank tx description contains search_text  →
  //   pair with oldest action whose (desc, amt) group matches the rule's target.
  // ════════════════════════════════════════════════════════════════════════════
  for (const tx of sortedBankTx) {
    if (usedBankTxIds.has(tx.id)) continue;

    const txDescNorm = normaliseText(tx.description);

    for (const { rule, descLower: targetDescLower, amt: targetAmt } of rulesWithKeys) {
      const needle = normaliseText(rule.search_text);
      if (!needle || !txDescNorm.includes(needle)) continue;

      // Find oldest action in the same group (description + amount)
      const action = popOldest(
        (a) =>
          roundAmount(a.amount) === targetAmt &&
          normaliseText(a.description) === targetDescLower
      );
      if (!action) continue; // no available line for this group → try next rule

      suggestions.push({
        ruleId: rule.id,
        bankTx: tx,
        expectedAction: action,
        matchLevel: 1,
        matchReason: `חוק מותאם אישית: "${rule.search_text}"`,
      });
      usedBankTxIds.add(tx.id);
      break;
    }
  }

  // ════════════════════════════════════════════════════════════════════════════
  // PASS 2 – Tier 2: Smart Match (same |amount| + description overlap OR same category)
  // ════════════════════════════════════════════════════════════════════════════
  for (const tx of sortedBankTx) {
    if (usedBankTxIds.has(tx.id)) continue;

    const txAmt  = roundAmount(tx.amount);
    const txDesc = tx.description ?? '';
    const txCat  = tx.accounting_category_id ?? '';

    const action = popOldest((a) => {
      if (roundAmount(a.amount) !== txAmt) return false;
      const sameCategory =
        txCat !== '' && a.accounting_category_id !== '' && a.accounting_category_id === txCat;
      const overlapAB = descriptionOverlap(txDesc, a.description);
      const overlapBA = descriptionOverlap(a.description, txDesc);
      return sameCategory || overlapAB || overlapBA;
    });
    if (!action) continue;

    const sameCat =
      txCat !== '' && action.accounting_category_id === txCat;
    suggestions.push({
      ruleId: null,
      bankTx: tx,
      expectedAction: action,
      matchLevel: 2,
      matchReason: sameCat ? 'סכום זהה + קטגוריה חשבונאית זהה' : 'סכום זהה + תיאור דומה',
    });
    usedBankTxIds.add(tx.id);
  }

  // ════════════════════════════════════════════════════════════════════════════
  // PASS 3 – Tier 3: Amount Only (unchecked by default in UI)
  // ════════════════════════════════════════════════════════════════════════════
  for (const tx of sortedBankTx) {
    if (usedBankTxIds.has(tx.id)) continue;

    const txAmt = roundAmount(tx.amount);
    const action = popOldest((a) => roundAmount(a.amount) === txAmt);
    if (!action) continue;

    suggestions.push({
      ruleId: null,
      bankTx: tx,
      expectedAction: action,
      matchLevel: 3,
      matchReason: 'סכום זהה בלבד (ללא תיאור / קטגוריה תואמים)',
    });
    usedBankTxIds.add(tx.id);
  }

  // Sort: tier 1 → 2 → 3; within tier keep FIFO bank-tx order
  suggestions.sort((a, b) => a.matchLevel - b.matchLevel);
  return suggestions;
}
